import { useEffect, useRef, useState } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { hero } from "../constants";
import { heroVideo } from "../utils";

const Hero = () => {
  const videoRef = useRef(null);
  const coverScaleRef = useRef(1.25); // échelle "plein écran" (calculée selon la vidéo)
  // Le dézoom-révélation ne concerne QUE le format téléphone (< 640px = breakpoint sm Tailwind).
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" &&
      window.matchMedia("(max-width: 639px)").matches
  );

  // Suit le passage mobile <-> bureau
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Calcule le zoom nécessaire pour que la vidéo remplisse tout l'écran (effet cover)
  const computeCoverScale = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.clientWidth) return;
    const containerAspect = v.clientWidth / v.clientHeight;
    const videoAspect = v.videoWidth / v.videoHeight;
    coverScaleRef.current = Math.max(
      videoAspect / containerAspect,
      containerAspect / videoAspect
    );
  };

  // Dézoom synchronisé sur la lecture : plein écran au début → voiture entière à la fin.
  // UNIQUEMENT en format téléphone ; sur bureau la vidéo reste en plein écran (cover).
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    if (!isMobile) {
      v.style.transform = "scale(1)";
      return;
    }

    let raf;
    const onMeta = () => computeCoverScale();
    const tick = () => {
      if (v.duration) {
        const p = Math.min(v.currentTime / v.duration, 1); // progression 0 → 1
        const eased = p * p; // reste plein écran, puis dézoome surtout vers la fin
        const scale = coverScaleRef.current - (coverScaleRef.current - 1) * eased;
        v.style.transform = `scale(${scale})`;
      }
      raf = requestAnimationFrame(tick);
    };

    // À la fin : on fige sur scale(1) → voiture entièrement visible (pas de retour brutal au zoom).
    const onEnded = () => {
      cancelAnimationFrame(raf);
      v.style.transform = "scale(1)";
    };

    v.addEventListener("loadedmetadata", onMeta);
    v.addEventListener("ended", onEnded);
    window.addEventListener("resize", computeCoverScale);
    if (v.readyState >= 1) computeCoverScale();
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      v.removeEventListener("loadedmetadata", onMeta);
      v.removeEventListener("ended", onEnded);
      window.removeEventListener("resize", computeCoverScale);
    };
  }, [isMobile]);

  useGSAP(() => {
    gsap.from(".hero-anim", {
      opacity: 0,
      y: 40,
      duration: 1,
      ease: "power2.out",
      stagger: 0.15,
      delay: 0.3,
    });
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden bg-noir"
    >
      {/* Vidéo de fond : public/ferrari-hero.mp4
          - Mobile : object-contain + dézoom pendant la lecture → voiture entière révélée à la fin.
          - Bureau : object-cover plein écran classique + boucle, sans dézoom. */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-center will-change-transform ${
          isMobile ? "object-contain" : "object-cover"
        }`}
        style={{ transform: `scale(${isMobile ? coverScaleRef.current : 1})` }}
        autoPlay
        loop={!isMobile}
        muted
        playsInline
        preload="auto"
      >
        <source src={heroVideo} type="video/mp4" />
      </video>

      {/* Voile sombre pour la lisibilité du texte */}
      <div className="hero-overlay" />

      {/* Contenu */}
      <div className="relative z-10 h-full section-container flex flex-col justify-center">
        <p className="hero-anim eyebrow">{hero.badge}</p>

        <h1 className="hero-anim hero-text-shadow font-display font-extrabold tracking-tight text-4xl sm:text-6xl md:text-7xl leading-[1.05] max-w-4xl">
          {hero.title}
        </h1>

        <p className="hero-anim hero-text-shadow mt-6 text-chrome-light text-base sm:text-lg max-w-xl">
          {hero.subtitle}
        </p>

        <div className="hero-anim mt-10 flex flex-col sm:flex-row gap-4">
          <a href="#rendez-vous" className="btn-primary">
            {hero.ctaPrimary}
          </a>
          <a href="#prestations" className="btn-secondary">
            {hero.ctaSecondary}
          </a>
        </div>
      </div>

      {/* Indicateur de scroll */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2 text-chrome-dark">
        <span className="text-xs uppercase tracking-widest">Défiler</span>
        <span className="w-px h-10 bg-gradient-to-b from-chrome-dark to-transparent animate-pulse" />
      </div>
    </section>
  );
};

export default Hero;
